import React, {
    createContext,
    useState,
    useContext,
    useEffect,
    useCallback,
    useRef
} from "react";

import api from "./api/api";

const AcademicYearContext =
    createContext(null);

export const AcademicYearProvider = ({
    children
}) => {

    const [
        academicYears,
        setAcademicYears
    ] = useState([]);

    const [
        selectedYear,
        setSelectedYearState
    ] = useState(null);

    const [
        loading,
        setLoading
    ] = useState(false);

    const [
        initialized,
        setInitialized
    ] = useState(false);

    const requestInProgress =
        useRef(false);

    const isMounted =
        useRef(true);

    /*
     * --------------------------------------------------
     * CHECK AUTHENTICATION
     * --------------------------------------------------
     */

    const isAuthenticated = useCallback(() => {

        return Boolean(
            localStorage.getItem("access")
        );

    }, []);

    /*
     * --------------------------------------------------
     * LOAD SAVED YEAR
     * --------------------------------------------------
     */

    const getSavedYear = useCallback(() => {

        try {

            const savedYear =
                localStorage.getItem(
                    "selectedYear"
                );

            return savedYear
                ? JSON.parse(savedYear)
                : null;

        } catch (error) {

            console.error(
                "Invalid saved academic year:",
                error
            );

            localStorage.removeItem(
                "selectedYear"
            );

            return null;

        }

    }, []);

    /*
     * --------------------------------------------------
     * SET SELECTED YEAR
     * --------------------------------------------------
     */

    const setSelectedYear = useCallback(
        (year) => {

            setSelectedYearState(
                year || null
            );

            if (year) {

                localStorage.setItem(
                    "selectedYear",
                    JSON.stringify(year)
                );

            } else {

                localStorage.removeItem(
                    "selectedYear"
                );

            }

        },
        []
    );

    /*
     * --------------------------------------------------
     * FETCH ACADEMIC YEARS
     * --------------------------------------------------
     */

    const fetchAcademicYears =
        useCallback(
            async ({
                silent = false
            } = {}) => {

                /*
                 * Prevent duplicate simultaneous
                 * requests
                 */
                if (
                    requestInProgress.current
                ) {

                    return;

                }

                /*
                 * Do not request without token
                 */
                if (
                    !isAuthenticated()
                ) {

                    if (
                        isMounted.current
                    ) {

                        setAcademicYears([]);

                        setSelectedYearState(
                            null
                        );

                        setInitialized(
                            true
                        );

                        setLoading(
                            false
                        );

                    }

                    return;

                }

                try {

                    requestInProgress.current =
                        true;

                    if (
                        !silent &&
                        isMounted.current
                    ) {

                        setLoading(
                            true
                        );

                    }

                    const response =
                        await api.get(
                            "/academic-years/"
                        );

                    const years =
                        Array.isArray(
                            response.data
                        )
                            ? response.data
                            : response.data?.results || [];

                    if (
                        !isMounted.current
                    ) {

                        return;

                    }

                    setAcademicYears(
                        years
                    );

                    /*
                     * Read the most recent saved
                     * selection from localStorage
                     */
                    const savedYear =
                        getSavedYear();

                    /*
                     * Match the saved year against
                     * fresh backend data
                     */
                    const savedYearFromApi =
                        years.find(
                            year =>
                                year.id ===
                                savedYear?.id
                        );

                    /*
                     * Find backend active year
                     */
                    const activeYear =
                        years.find(
                            year =>
                                year.is_active ===
                                true
                        );

                    /*
                     * Selection priority:
                     *
                     * 1. Saved valid selection
                     * 2. Current selected valid year
                     * 3. Backend active year
                     * 4. First available year
                     */
                    setSelectedYearState(
                        previousYear => {

                            const currentYear =
                                years.find(
                                    year =>
                                        year.id ===
                                        previousYear?.id
                                );

                            const finalYear =
                                savedYearFromApi ||
                                currentYear ||
                                activeYear ||
                                years[0] ||
                                null;

                            if (
                                finalYear
                            ) {

                                localStorage.setItem(
                                    "selectedYear",
                                    JSON.stringify(
                                        finalYear
                                    )
                                );

                            } else {

                                localStorage.removeItem(
                                    "selectedYear"
                                );

                            }

                            return finalYear;

                        }
                    );

                    setInitialized(
                        true
                    );

                } catch (error) {

                    /*
                     * If the token expired, clear
                     * the academic year state.
                     */
                    if (
                        error.response?.status ===
                        401
                    ) {

                        setAcademicYears([]);

                        setSelectedYearState(
                            null
                        );

                        localStorage.removeItem(
                            "selectedYear"
                        );

                    }

                    console.error(
                        "Failed to fetch academic years:",
                        error
                    );

                } finally {

                    requestInProgress.current =
                        false;

                    if (
                        isMounted.current
                    ) {

                        setLoading(
                            false
                        );

                    }

                }

            },
            [
                getSavedYear,
                isAuthenticated
            ]
        );

    /*
     * --------------------------------------------------
     * INITIALIZATION
     *
     * Instead of relying only on a login event,
     * check authentication whenever the provider
     * mounts.
     * --------------------------------------------------
     */

    useEffect(() => {

        isMounted.current =
            true;

        fetchAcademicYears();

        return () => {

            isMounted.current =
                false;

        };

    }, [
        fetchAcademicYears
    ]);

    /*
     * --------------------------------------------------
     * AUTH STATE CHANGE
     * --------------------------------------------------
     */

    useEffect(() => {

        const handleLogin = () => {

            /*
             * Small delay ensures the token has
             * already been written to localStorage
             */
            setTimeout(() => {

                fetchAcademicYears();

            }, 50);

        };

        const handleLogout = () => {

            setAcademicYears([]);

            setSelectedYearState(
                null
            );

            setInitialized(
                true
            );

            setLoading(
                false
            );

            localStorage.removeItem(
                "selectedYear"
            );

        };

        window.addEventListener(
            "user-logged-in",
            handleLogin
        );

        window.addEventListener(
            "force-logout",
            handleLogout
        );

        return () => {

            window.removeEventListener(
                "user-logged-in",
                handleLogin
            );

            window.removeEventListener(
                "force-logout",
                handleLogout
            );

        };

    }, [
        fetchAcademicYears
    ]);

    /*
     * --------------------------------------------------
     * ACADEMIC YEAR UPDATE EVENT
     * --------------------------------------------------
     */

    useEffect(() => {

        const handleAcademicYearUpdate =
            () => {

                fetchAcademicYears({
                    silent: true
                });

            };

        window.addEventListener(
            "academic-year-updated",
            handleAcademicYearUpdate
        );

        return () => {

            window.removeEventListener(
                "academic-year-updated",
                handleAcademicYearUpdate
            );

        };

    }, [
        fetchAcademicYears
    ]);

    /*
     * --------------------------------------------------
     * ACTIVATE YEAR
     * --------------------------------------------------
     */

    const activateYear =
        useCallback(
            async (
                yearId
            ) => {

                const response =
                    await api.patch(
                        `/academic-years/${yearId}/activate/`
                    );

                /*
                 * Use the backend response
                 * immediately
                 */
                const activatedYear =
                    response.data?.academic_year;

                if (
                    activatedYear
                ) {

                    setSelectedYear(
                        activatedYear
                    );

                }

                /*
                 * Refresh the full list once
                 */
                await fetchAcademicYears();

                return activatedYear;

            },
            [
                fetchAcademicYears,
                setSelectedYear
            ]
        );

    /*
     * --------------------------------------------------
     * MANUAL YEAR SELECTION
     * --------------------------------------------------
     */

    const selectYear =
        useCallback(
            (year) => {

                if (!year) {

                    return;

                }

                const freshYear =
                    academicYears.find(
                        item =>
                            item.id ===
                            year.id
                    );

                setSelectedYear(
                    freshYear || year
                );

            },
            [
                academicYears,
                setSelectedYear
            ]
        );

    return (

        <AcademicYearContext.Provider
            value={{

                academicYears,

                selectedYear,

                setSelectedYear:
                    selectYear,

                fetchAcademicYears,

                activateYear,

                loading,

                initialized

            }}
        >

            {children}

        </AcademicYearContext.Provider>

    );

};

export const useAcademicYear =
    () => {

        const context =
            useContext(
                AcademicYearContext
            );

        if (
            !context
        ) {

            throw new Error(
                "useAcademicYear must be used inside AcademicYearProvider"
            );

        }

        return context;

    };

